// ApplicationStateful.jsx
import React, { useEffect, useState } from "react";
import { Provider, useDispatch } from "react-redux";
import store from "../../state/store";
// ⬇️ make sure this path matches where your slice really lives

import { RouterSwitch } from "./RouterSwitch";
import { Route } from "react-router";
import { useTranslation } from "react-i18next";
import Drawer from "./drawer/Drawer";
import Content from "./Content";
import Notifications from "../../../../common/src/components/widget/Notifications";
import Error404 from "./Error404";
import TopBar from "./TopBar";
import UserList from "../pages/manage_users/UserList";
import UserForm from "../pages/manage_users/UserForm";
import UserProfile from "../pages/profile/UserProfile";
import GroupList from "../pages/manage_users/group/GroupList";
import GroupForm from "../pages/manage_users/group/GroupForm";
import { UserContext } from "../../context/UserContext";
import ContactAdministratorError from "./ContactAdministratorError";
import BreadCrumbsStateful from "./BreadCrumbsStateful";
import Dashboard from "../pages/dashboards/Dashboard";
import DatasetList from "../pages/dataset/DatasetList";
import DatasetForm from "../pages/dataset/DatasetForm";
import AccessLog from "../pages/audit/AccessLog";
import { ModalProvider } from "../../context/ModalProvider";
import { Role } from "../../auth/roles";
import { ModalProvider1 } from "common/src/hooks/modal/ModalProvider";
import Dashboards from "../pages/dashboards/Dashboards";
import {initFromEnv} from "../../state/slice/dataModeSlice";
import Auth from "../../auth/Auth";
// If Drawer needs location, use useLocation in Drawer instead of this.props

export const RouteContext = React.createContext(null);

/** Wrap AppContent with Provider so Redux hooks have context */
const ApplicationStateful = () => {
  const routesState = useState({ routes: [], breadcrumbs: [] });
  const [user, setUser] = useState({});

  return (
      <RouteContext.Provider value={routesState}>
        <UserContext.Provider value={{ user, setUser }}>
          <Provider store={store}>
            <ModalProvider>
              <ModalProvider1>
                <Auth>
                  <AppContent />
                </Auth>
              </ModalProvider1>
            </ModalProvider>
          </Provider>
        </UserContext.Provider>
      </RouteContext.Provider>
  );
};

const AppContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    // derive availability + coerce mode from window._env_
    dispatch(initFromEnv());
  }, [dispatch]);

  return (
      <>
        <Notifications>
          <TopBar />
          {/* Don't pass this.props here; use useLocation inside Drawer if needed */}
          <Drawer />
          <div id="main-section" className="is-main-section-container">
            <Content>
              <BreadCrumbsStateful />
              <RouterSwitch>
                <Route path="/" element={<Dashboard />}>
                  <Route index element={<Dashboards />} />
                </Route>

                <Route
                    path="/dashboards"
                    name={t("breadcrumbs.dashboards")}
                    element={
                      <Dashboard hasOneOfRequiredRoles={[
                        Role.AUDIT_READ, Role.ADMIN_GROUPS, Role.ADMIN_USERS,
                        Role.DATALAKE_ACCESS, Role.DATASET_CREATE,
                        Role.DATASET_DELETE, Role.DATASET_PUBLISH
                      ]}/>
                    }
                >
                  <Route index element={<Dashboards />} />
                </Route>

                <Route
                    path="/dataset"
                    name={t("breadcrumbs.dataset")}
                    element={<Dashboard requiredRoles={[Role.DATASET_CREATE]} />}
                >
                  <Route index element={<DatasetList />} />
                  <Route path="edit/:datasetId" name={t("edit")} element={<DatasetForm />} />
                  <Route path="create" name={t("add")} element={<DatasetForm />} />
                </Route>

                <Route
                    path="/users"
                    name={t("breadcrumbs.users")}
                    element={<Dashboard hasOneOfRequiredRoles={[Role.ADMIN_USERS, Role.ADMIN_GROUPS]} />}
                >
                  <Route index element={<UserList />} />
                  <Route path="edit/:userId" name={t("edit")} element={<UserForm />} />
                  <Route path="create" name={t("add")} element={<UserForm />} />
                </Route>

                <Route
                    path="groups"
                    name={t("breadcrumbs.groups")}
                    element={<Dashboard hasOneOfRequiredRoles={[Role.ADMIN_USERS, Role.ADMIN_GROUPS]} />}
                >
                  <Route index element={<GroupList />} />
                  <Route path="edit/:groupId" name={t("edit")} element={<GroupForm />} />
                  <Route path="create" name={t("add")} element={<GroupForm />} />
                </Route>

                <Route
                    path="audit"
                    name={t("breadcrumbs.audit_log")}
                    element={<Dashboard hasOneOfRequiredRoles={[Role.AUDIT_READ]} hasAllOfRequiredRoles={[Role.AUDIT_READ]} />}
                >
                  <Route index element={<AccessLog />} />
                </Route>

                <Route path="/profile" name={t("user_profile.profile")} element={<UserProfile />} />

                <Route path="account-error" element={<ContactAdministratorError />} />
                <Route path="*" element={<Error404 />} breadcrumb={false} />
              </RouterSwitch>
            </Content>
          </div>
        </Notifications>
      </>
  );
};

export default ApplicationStateful;
